import { useState } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { format } from 'date-fns';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import {
  Badge,
  badgeStyle,
  badgeType,
  btnSize,
  btnType,
  Button,
  colors,
  fontWeight,
  Heading,
  headingLevel,
  Input,
  inputSize,
  Select,
  selectType,
  Text,
  textLevel,
} from '../../../../packages';
import { Drawer, drawerPosition, drawerType } from '../../../../packages/ui/organisms/drawer';
import { ProfileInfo } from '../../../../packages/ui/templates/profile-info';
import { sentenceCase } from 'sentence-case';
import { CLAIM_STATUSES } from '../../claims/Claims.constants';
import DatePickerInput from './DatePicker';
import { useStyles } from '../ClaimDetail.styles';
import { Stack, Typography } from '@mui/material';

const defaultData = {
  claimStatus: '',
  datePaid: '',
  amountPaid: 0,
  amountDenied: 0,
  notes: '',
  trackingNumber: '',
  initialBillingDate: '',
};

const labels = {
  claimStatus: 'Claim Status',
  amountPaid: 'Amount Paid',
  datePaid: 'Date Paid',
  amountDenied: 'Amount Denied',
  notes: 'Notes',
  trackingNumber: 'Tracking Number',
  initialBillingDate: 'Initial Billing Date',
};

export const StatusDetailDrawer = ({ data, open, onClose, onSubmit }) => {
  const classes = useStyles();
  const [formData, setFormData] = useState({ ...data });
  const [errors, setErrors] = useState(defaultData);

  const onChangeField = field => e => {
    if (errors[field]) {
      setErrors(s => ({ ...s, [field]: '' }));
    }
    setFormData(old => ({
      ...old,
      [field]: e.target?.value,
    }));
  };

  const onChangeStatus = () => {
    const { amountPaid, claimStatus, datePaid, amountDenied, notes, trackingNumber, initialBillingDate } =
      formData;
    onSubmit(
      { amountPaid, claimStatus, datePaid, amountDenied, notes, trackingNumber, initialBillingDate },
      'statusDetail',
    );
  };

  const onChangeBillingDate = date => {
    if (date?.isValid() && date?.isBefore(dayjs())) {
      setFormData({ ...formData, initialBillingDate: date.toISOString() });
    } else {
      setFormData({ ...formData, initialBillingDate: '' });
    }
  };

  const onChangePaidDate = date => {
    if (date?.isValid() && date?.isBefore(dayjs())) {
      setFormData({ ...formData, datePaid: date.toISOString() });
    } else {
      setFormData({ ...formData, datePaid: '' });
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      variant={drawerType.FORM}
      position={drawerPosition.RIGHT}
      title="Edit Status"
      submitBtnTitle="Update"
      onSubmit={onChangeStatus}
    >
      <Box className={classes.inputWrapper}>
        <label htmlFor="claimStatus">
          <Text level={textLevel.S} weight={fontWeight.BOLD}>
            {labels.claimStatus}
          </Text>
        </label>
        <Select
          value={formData.claimStatus}
          name="claimStatus"
          variant={selectType.SECONDARY}
          emptyText=""
          displayEmpty
          options={CLAIM_STATUSES}
          onChange={onChangeField('claimStatus')}
        />
      </Box>
      <Box className={classes.inputWrapper}>
        <label htmlFor="initialBillingDate">
          <Text level={textLevel.S} weight={fontWeight.BOLD}>
            {labels.initialBillingDate}
          </Text>
        </label>
        <DatePickerInput date={dayjs(formData?.initialBillingDate)} onChange={onChangeBillingDate} />
      </Box>
      <Box className={classes.inputWrapper}>
        <label htmlFor="amountPaid">
          <Text level={textLevel.S} weight={fontWeight.BOLD}>
            {labels.amountPaid}
          </Text>
        </label>
        <Input
          name="amountPaid"
          value={formData.amountPaid || ''}
          onChange={onChangeField('amountPaid')}
          size={inputSize.M}
          placeholder="Enter your amount"
          type="number"
        />
      </Box>
      <Box className={classes.inputWrapper}>
        <label htmlFor="datePaid">
          <Text level={textLevel.S} weight={fontWeight.BOLD}>
            {labels.datePaid}
          </Text>
        </label>
        <DatePickerInput date={dayjs(formData.datePaid)} onChange={onChangePaidDate} />
      </Box>

      <Box className={classes.inputWrapper}>
        <label htmlFor="amountPaid">
          <Text level={textLevel.S} weight={fontWeight.BOLD}>
            {labels.amountDenied}
          </Text>
        </label>
        <Input
          name="amountDenied"
          value={formData.amountDenied || ''}
          onChange={onChangeField('amountDenied')}
          size={inputSize.M}
          placeholder="Enter your amount"
          type="number"
        />
      </Box>
      <Box className={classes.inputWrapper}>
        <label htmlFor="notes">
          <Text level={textLevel.S} weight={fontWeight.BOLD}>
            {labels.notes}
          </Text>
        </label>
        <Input
          name="notes"
          value={formData.notes || ''}
          onChange={onChangeField('notes')}
          size={inputSize.M}
          placeholder="Enter your notes"
        />
      </Box>
      <Box className={classes.inputWrapper}>
        <label htmlFor="notes">
          <Text level={textLevel.S} weight={fontWeight.BOLD}>
            {labels.trackingNumber}
          </Text>
        </label>
        <Input
          name="trackingNumber"
          value={formData.trackingNumber || ''}
          onChange={onChangeField('trackingNumber')}
          size={inputSize.M}
          placeholder="Enter your tracking number"
        />
      </Box>
    </Drawer>
  );
};

const ClaimStatus = ({ onSubmit, data = defaultData }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const SectionTitle = ({ title = '' }) => (
    <Typography
      sx={{
        fontSize: '12px',
        fontWeight: 800,
        lineHeight: '20px',
        letterSpacing: '1px',
        width: '100%',
        textTransform: 'uppercase',
        color: colors.neutral900,
      }}
    >
      {title}
    </Typography>
  );

  const Col = ({
    label,
    value,
    isDate = false,
    colType: badge = false,
    isError = false,
    warning = false,
    isMultiple = false,
    primary = false,
  }) => {
    const dayDifference = isDate ? Math.abs(dayjs(value).diff(dayjs(), 'days')) : 0;
    if (isDate && !value) {
      return null;
    }
    let isWarning = warning;
    if (!isWarning && !value) {
      isWarning = true;
    }
    const isStale = dayDifference > 30;
    return (
      <Box display="flex" flexDirection="column" minWidth="160px">
        <Text className={classes.colLabel} level={textLevel.S} weight={fontWeight.SEMI_BOLD}>
          {label}
        </Text>
        <>
          {isWarning ? (
            <Typography
              sx={{
                fontFamily: 'Manrope',
                fontSize: '16px',
                fontWeight: 500,
                lineHeight: '24px',
                color: colors.orange500,
                marginTop: '8px',
              }}
            >
              Waiting for payer
            </Typography>
          ) : badge ? (
            <Badge
              variant={badgeType.OUTLINED}
              style={
                value === 'PAID' ||
                value === 'PARTIALLY_PAID' ||
                value === 'OVERPAID' ||
                value === 'UNDERPAID' ||
                value === 'APPEALED'
                  ? badgeStyle.RESOLVED
                  : value === 'CREATED' || value === 'COMPLETED'
                  ? badgeStyle.PRIMARY
                  : value === 'DENIED' || value === 'REJECTED'
                  ? badgeStyle.HIGH
                  : badgeStyle.MEDIUM
              }
              className={clsx(
                classes.badge,
                { [classes.unPaid]: value === 'DENIED' || value === 'REJECTED' },
                {
                  [classes.submittedStatus]: value === 'CREATED' || value === 'COMPLETED',
                },
                {
                  [classes.paid]:
                    value === 'PAID' ||
                    value === 'PARTIALLY_PAID' ||
                    value === 'OVERPAID' ||
                    value === 'UNDERPAID' ||
                    value === 'APPEALED',
                },
                classes.marginTop8,
              )}
            >
              {sentenceCase(value || '')}
            </Badge>
          ) : isMultiple ? (
            value?.map((v, i) => (
              <Text
                key={`text-${i}`}
                className={clsx(classes.marginTop8, {
                  [classes.primaryColor]: primary,
                  [classes.errorColor]: v?.includes('fail'),
                })}
                level={textLevel.M}
                weight={fontWeight.MEDIUM}
              >
                {v}
              </Text>
            ))
          ) : (
            <Text
              className={clsx(classes.marginTop8, {
                [classes.primaryColor]: primary,
                [classes.errorColor]: value?.includes('fail'),
              })}
              level={textLevel.M}
              weight={fontWeight.MEDIUM}
            >
              {isDate ? dayjs(value).format('M/D/YY') : value || 'N/A'}
              {dayDifference > 0 && (
                <Box
                  sx={{
                    display: 'inline',
                    marginLeft: '12px',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: isStale ? colors.destructive500 : 'inherit',
                  }}
                >
                  {dayDifference} days
                </Box>
              )}
            </Text>
          )}
        </>
      </Box>
    );
  };
  const hasCpt = data?.cptCodes && data?.cptCodes.length > 0;
  const isStale = () => {
    if (data?.dateOfService) {
      const dayDifference = Math.abs(dayjs(data.dateOfService).diff(dayjs(), 'days'));
      if (dayDifference > 30) {
        return true;
      }
    }
    if (data?.updatedAt) {
      const dayDifference = Math.abs(dayjs(data.updatedAt).diff(dayjs(), 'days'));
      if (dayDifference > 30) {
        return true;
      }
    }
    if (data?.submitted) {
      const dayDifference = Math.abs(dayjs(data.submitted).diff(dayjs(), 'days'));
      if (dayDifference > 30) {
        return true;
      }
    }
    if (data?.datePaid) {
      const dayDifference = Math.abs(dayjs(data.datePaid).diff(dayjs(), 'days'));
      if (dayDifference > 30) {
        return true;
      }
    }
    return false;
  };

  const renderSubmissionFailure = () => {
    if (data?.candidStatus !== 'CANDID_FAILURE') {
      return null;
    }
    if (data?.candidErrorDetails) {
      let messages = [];
      try {
        const errorDetail = JSON.parse(data?.candidErrorDetails);
        if (errorDetail && errorDetail.content) {
          Array.isArray(errorDetail.content) || (errorDetail.content = [errorDetail.content]);
          messages = errorDetail.content.map(
            error => `Field '${error.fieldName}' ${error.humanReadableMessage}`,
          );
        }
      } catch (e) {
        messages = [data?.candidErrorDetails || 'Unknown error'];
      }
      return (
        <Box display="flex" px={4} pb={4} flexWrap="wrap" gap={3} rowGap={4}>
          <Col label="Submission Failure Reason" value={messages} isMultiple />
        </Box>
      );
    }
    return null;
  };

  let allowedAmount =
    data?.candidResponse?.['service_lines']
      ?.map(svcLine => svcLine.allowed_amount_cents)
      ?.reduce((accumulator, currentValue) => {
        return accumulator + currentValue;
      }, 0) ?? null;
  allowedAmount = allowedAmount ? (allowedAmount / 100).toFixed(2) : null;
  let paidAmount =
    data?.candidResponse?.['service_lines']
      ?.map(svcLine => svcLine.paid_amount_cents)
      ?.reduce((accumulator, currentValue) => {
        return accumulator + currentValue;
      }, 0) ?? null;
  paidAmount = paidAmount ? (paidAmount / 100).toFixed(2) : null;
  let patientResponsibility =
    data?.candidResponse?.['service_lines']
      ?.map(svcLine => svcLine.patient_responsibility_cents)
      ?.reduce((accumulator, currentValue) => {
        return accumulator + currentValue;
      }, 0) ?? null;
  patientResponsibility = patientResponsibility ? (patientResponsibility / 100).toFixed(2) : null;
  const hasInsuranceDenial =
    data?.candidResponse?.['service_lines']
      ?.map(svcLine => svcLine.denial_reason?.reason)
      .filter(reason => reason)?.length > 0;
  const denialReasons = hasInsuranceDenial
    ? data?.candidResponse?.['service_lines']
        ?.map(svcLine => svcLine.denial_reason?.reason)
        .filter(reason => reason)
        .join(', ')
    : null;
  return (
    <Box
      sx={{
        background: colors.white,
        boxShadow:
          '0px 4px 8px rgba(0, 0, 0, 0.04), 0px 0px 2px rgba(0, 0, 0, 0.06), 0px 0px 1px rgba(0, 0, 0, 0.04)',
        borderRadius: 2,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" pl={3} py="12px" pr={2}>
        <Stack direction="row" gap={2} alignItems="center">
          <Typography sx={{ fontSize: '18px', fontWeight: 600, lineHeight: '24px' }}>Claim Status</Typography>
          <Stack direction="row" gap={1}>
            <Badge
              style={data?.status === 'CANDID_FAILURE' ? badgeStyle.CRITICAL : badgeStyle.PRIMARY_ACTIVE}
              variant={badgeType.FILLED}
              size="28px"
            >
              {sentenceCase(data?.status || '')}
            </Badge>
            {isStale() && (
              <Badge style={badgeStyle.STALE} variant={badgeType.OUTLINED} size="28px">
                Stale
              </Badge>
            )}
          </Stack>
        </Stack>

        <Button
          className={classes.btn}
          variant={btnType.TEXT}
          onClick={() => setOpen(true)}
          size={btnSize.SMALL}
        >
          Edit info
        </Button>
      </Box>
      <Divider />

      <Box display="flex" p={4} flexWrap="wrap" gap={3} rowGap={4}>
        <Col label="Date of Service" value={data?.dateOfService} isDate />
        <Col label="CPT Codes" value={hasCpt ? data.cptCodes.join(', ') : 'Cpt Code Missing'} />
        <Col
          label="Modifiers"
          value={
            data?.candidResponse?.['service_lines']
              ?.map(serviceLine => serviceLine.modifiers)
              ?.flat()
              .join(', ') ?? ''
          }
        />
        {hasCpt && <Col label="Diagnosis" value={data.icd10Code || ''} />}
        <Col label="Place of Service" value={'02 - Telehealth'} />
        <Col
          label="Claim Status"
          value={
            data?.candidResponse?.status
              ? sentenceCase(
                  `${data.candidResponse?.status.charAt(0).toUpperCase() || ''}${
                    data.candidResponse?.status.substring(1).toLowerCase() || ''
                  }`,
                )
              : sentenceCase(data?.candidStatus || '')
          }
          primary
        />
        <Col label="Date Updated" value={data?.syncDetails?.syncedAt} isDate />
        <Col label="Date Submitted" value={data?.syncDetails?.candidFullResponse?.created_at} isDate />
        <Col label="Date Paid" value={data?.datePaid} isDate />
      </Box>
      {(!data?.candidResponse?.status ||
        (data?.candidResponse.status !== 'paid' && data?.candidResponse.status !== 'finalized_paid')) &&
        hasInsuranceDenial && (
          <Box display="flex" px={4} pb={4} flexWrap="wrap" gap={3} rowGap={4}>
            <Col label="Denial/Rejection Reason" value={denialReasons} />
          </Box>
        )}
      {renderSubmissionFailure()}
      <Box display="flex" px={4} pb={4} flexWrap="wrap" gap={3} rowGap={4}>
        <SectionTitle title="Charges & Payments" />
        <Col label="Amount Billed" value={data?.amountBilled} />
        <Col label="Amount Allowed" value={allowedAmount ? `$${allowedAmount}` : null} />
        <Col label="Insurance Paid" value={paidAmount ? `$${allowedAmount}` : null} />
        {hasInsuranceDenial && <Col label="Insurance Denied" value={denialReasons} />}

        <Col
          label="Patient Responsibility"
          value={patientResponsibility ? `$${patientResponsibility}` : 'None'}
        />
        {patientResponsibility && <Col label="Patient Invoiced" value={data?.invoice ? 'Yes' : 'No'} />}
        {patientResponsibility && (
          <Col
            label="Patient Paid"
            value={data?.invoice?.status === 'PAID' ? `$${data?.invoice?.memberAmount}` : '$0'}
          />
        )}
        {patientResponsibility && (
          <Col
            label="Patient Balance"
            value={
              data?.invoice
                ? data?.invoice?.status !== 'PAID'
                  ? `$${data?.invoice?.memberAmount}`
                  : '$0'
                : `$${patientResponsibility}`
            }
          />
        )}
        <SectionTitle title="Payer Information" />
        <Col label="Payer" value={data?.payerName} />
        <Col label="Tracking No" value={data?.trackingNumber} />
        <Col label="External Claim ID" value={data?.externalClaimId} />
      </Box>
      {open && (
        <StatusDetailDrawer data={data} open={open} onClose={() => setOpen(false)} onSubmit={onSubmit} />
      )}
    </Box>
  );
};

export default ClaimStatus;
